import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { ContadorService } from '../../../services/contador/contador.service';
import { MinhaSituacaoContadorDTO } from '../../../models/contador/contador.model';

/**
 * Painel "Seja um Contador Parceiro" (miolo compartilhado).
 *
 * Encapsula a logica de solicitar virar contador parceiro: carrega a situacao
 * do usuario autenticado (`minha-situacao`) e renderiza condicionalmente —
 * ja-contador, solicitacao em analise ou formulario de solicitacao (observacao
 * opcional). Reutilizado pela page `/contador/solicitar` e pela Area do Contador.
 *
 * Fonte da verdade: `API_CONTRACT_contador.md`.
 */
@Component({
  selector: 'app-solicitar-contador-panel',
  templateUrl: './solicitar-contador-panel.component.html',
  styleUrls: ['./solicitar-contador-panel.component.scss'],
  providers: [MessageService],
})
export class SolicitarContadorPanelComponent implements OnInit {
  /**
   * Quando `true` (uso embutido na Area do Contador), oculta o botao "Ir para a
   * Area do Contador" do estado "ja e contador" — a pessoa ja esta na area.
   */
  @Input() embutido = false;

  /** Emitido apos solicitar com sucesso (e apos a situacao convergir). */
  @Output() solicitado = new EventEmitter<void>();

  /** Situacao atual do usuario (null enquanto carrega ou em erro). */
  situacao: MinhaSituacaoContadorDTO | null = null;

  loading = true;
  erroCarregar = false;

  /** Observacao opcional digitada pelo usuario. */
  observacao = '';
  enviando = false;

  constructor(
    private contadorService: ContadorService,
    private messageService: MessageService,
    private translate: TranslateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.carregarSituacao();
  }

  /** Consulta a situacao do usuario autenticado. */
  carregarSituacao(): void {
    this.loading = true;
    this.erroCarregar = false;

    this.contadorService.minhaSituacao().subscribe({
      next: (res) => {
        this.situacao = res;
        this.loading = false;
      },
      error: () => {
        this.situacao = null;
        this.erroCarregar = true;
        this.loading = false;
      },
    });
  }

  /** Envia a solicitacao para virar contador parceiro. */
  solicitar(): void {
    if (this.enviando) {
      return;
    }

    this.enviando = true;
    const observacao = (this.observacao || '').trim() || undefined;

    this.contadorService.solicitar(observacao).subscribe({
      next: () => {
        this.enviando = false;
        this.observacao = '';
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('contador.solicitar.sucesso'),
        });
        // Recarrega para refletir o novo estado ("em analise").
        this.carregarSituacao();
        // Notifica o container (ex.: Area do Contador re-decide o branch).
        this.solicitado.emit();
      },
      error: (err) => {
        this.enviando = false;
        this.tratarErroSolicitacao(err);
      },
    });
  }

  /**
   * Trata erros do POST. Em 422 (ja-contador / ja-pendente) exibe a mensagem do
   * backend e recarrega a situacao, fazendo a UI convergir para o estado correto.
   */
  private tratarErroSolicitacao(err: any): void {
    const status = err?.status;
    if (status === 422) {
      this.messageService.add({
        severity: 'warn',
        summary: err?.error?.message || this.translate.instant('contador.erros.ja_pendente'),
      });
      this.carregarSituacao();
      return;
    }
    this.messageService.add({
      severity: 'error',
      summary: err?.error?.message || this.translate.instant('contador.erros.generico'),
    });
  }

  /** Navega para a area exclusiva do contador. */
  irParaArea(): void {
    this.router.navigateByUrl('/contador/area');
  }
}
